import React from "react";
import { useTranslation } from "react-i18next";

import "../../styles/theme.css";
import "../../styles/dashboard.css";
import "../../styles/ProfilesManager.css";
import "../../styles/helpProfile.css";

export default function Helpdos() {
  const { t } = useTranslation("help");

  return (
    <div className="theme-whbl">
      {/* Header */}

      <div className="container-fluid">
        <div className="row">
          <main
            style={{
              width: "97%",
              overflowX: "auto",
            }}
          >
            <div className="dashboard">
              {/* Page Header */}
              <div className="pm-header">
                <div className="pm-title-section">
                  <h1 className="pm-title">
                    {t(
                      "helpDos.title",
                      "AI-specific Denial-of-Service Profile"
                    )}
                  </h1>
                </div>
              </div>

              {/* ========================= */}
              {/* DoS Profile Help Content */}
              {/* ========================= */}

              {/* Step 1 */}
              <div>
                <p>
                  {t(
                    "helpDos.description",
                    "Use this profile type to evaluate whether the AI endpoint remains available and controlled under excessive, repeated, expensive, or malformed AI-specific workloads. Run disruptive settings only in an approved non-production environment."
                  )}
                </p>

                <img
                   src={t("helpDos.image")}
                  alt="DoS parameters"
                  style={{ padding: "10px 0" }}
                />
              </div>

              <table className="dark-guide-table">
                <thead>
                  <tr>
                    <th>
                      {t("helpDos.tableHeaders.control", "Control")}
                    </th>
                    <th>
                      {t("helpDos.tableHeaders.purpose", "Purpose")}
                    </th>
                    <th>
                      {t(
                        "helpDos.tableHeaders.detail",
                        "User-guide detail"
                      )}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(
                    t("helpDos.controls", {
                      returnObjects: true,
                      defaultValue: [
                        {
                          name: "API Endpoint",
                          purpose: "Target route for DoS-style requests.",
                          detail:
                            "Use a staging endpoint or controlled test route when possible. Confirm quotas, rate limits, and monitoring before running.",
                        },
                        {
                          name: "Token Overflow",
                          purpose:
                            "Checks behavior with unusually large prompt or token pressure.",
                          detail:
                            "Useful for validating request-size limits, truncation behavior, and graceful rejection.",
                        },
                        {
                          name: "Memory Exhaustion",
                          purpose:
                            "Checks whether large or repeated workloads strain server memory.",
                          detail:
                            "Use cautiously and watch infrastructure telemetry during the run.",
                        },
                        {
                          name: "Rate Limit Bypass",
                          purpose:
                            "Checks whether repeated calls can evade throttling.",
                          detail:
                            "Use to validate API gateway, application, and provider-side rate controls.",
                        },
                        {
                          name: "Slowloris AI",
                          purpose:
                            "Checks behavior under slow or held-open request patterns.",
                          detail:
                            "Useful for testing connection handling and timeout enforcement.",
                        },
                        {
                          name: "Cost Amplification",
                          purpose:
                            "Checks whether inputs can force disproportionate model cost.",
                          detail:
                            "Use for budget-risk validation and max-token/cost guardrails.",
                        },
                        {
                          name: "Service Disruption / Burst Attacks",
                          purpose:
                            "Checks availability under sudden request pressure.",
                          detail:
                            "Use to validate queueing, autoscaling, and rejection behavior.",
                        },
                        {
                          name: "Tool Execution Abuse",
                          purpose:
                            "Checks whether repeated tool calls or tool-triggering prompts cause excessive work.",
                          detail:
                            "Most relevant for agentic apps and MCP-backed systems.",
                        },
                        {
                          name: "Session Hijacking",
                          purpose:
                            "Checks whether session handling can be abused across requests.",
                          detail:
                            "Use where the endpoint uses user sessions, conversation IDs, or shared context.",
                        },
                        {
                          name: "Resource Exhaustion",
                          purpose:
                            "Checks whether repeated or resource-intensive requests consume excessive CPU, memory, connections, or other system resources.",
                          detail:
                            "Use to validate resource quotas, concurrency controls, workload limits, and graceful rejection under sustained load.",
                        },
                        {
                          name: "Prompt Injection",
                          purpose:
                            "Checks whether crafted prompts can manipulate the model into ignoring instructions, revealing restricted information, or performing unintended actions.",
                          detail:
                            "Use to validate system prompts, input filtering, output controls, and safeguards against malicious instructions.",
                        },
                        {
                          name: "Repetitive Requests",
                          purpose:
                            "Checks whether repeated identical or highly similar requests can cause unnecessary processing or resource consumption.",
                          detail:
                            "Use to validate duplicate-request handling, caching, throttling, and protections against repeated workload abuse.",
                        },
                        {
                          name: "Model Poisoning",
                          purpose:
                            "Checks whether manipulated training, fine-tuning, or model-related inputs can introduce malicious or unintended model behavior.",
                          detail:
                            "Use to validate model provenance, training-data controls, model integrity, and monitoring for unexpected behavioral changes.",
                        },
                        {
                          name: "Context Overflow",
                          purpose:
                            "Checks behavior when prompts, conversation history, or retrieved content exceed the model's supported context capacity.",
                          detail:
                            "Use to validate context-size limits, truncation behavior, request rejection, and graceful handling of oversized inputs.",
                        },
                      ],
                    }) || []
                  ).map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.name}</td>
                      <td>{item.purpose}</td>
                      <td>{item.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <style>
                {`
                  .dark-guide-table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                    font-family: "Segoe UI", Arial, sans-serif;
                    font-size: 14px;
                    color: #e5e7eb;
                    background: #1E293B;
                    border: 1px solid #374151;
                  }

                  .dark-guide-table thead th {
                    background: #1E293B;
                    color: #fff;
                    font-weight: 700;
                    text-align: left;
                    padding: 12px 14px;
                    border: 1px solid #4b5563;
                    vertical-align: middle;
                  }

                  .dark-guide-table tbody td {
                    background: #1E293B;
                    color: #e5e7eb;
                    padding: 12px 14px;
                    border: 1px solid #374151;
                    vertical-align: top;
                    line-height: 1.55;
                  }

                  .dark-guide-table tbody tr:nth-child(even) td {
                    background: #1E293B;
                  }

                  .dark-guide-table tbody tr:hover td {
                    background: #1E293B;
                  }

                  .dark-guide-table th:nth-child(1),
                  .dark-guide-table td:nth-child(1) {
                    width: 23%;
                  }

                  .dark-guide-table th:nth-child(2),
                  .dark-guide-table td:nth-child(2) {
                    width: 30%;
                  }

                  .dark-guide-table th:nth-child(3),
                  .dark-guide-table td:nth-child(3) {
                    width: 47%;
                  }

                  @media (max-width: 768px) {
                    .dark-guide-table {
                      min-width: 750px;
                    }
                  }
                `}
              </style>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}